import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { Dialog, DialogBody, DialogTitle } from "../dialog";
import { PreviewBlogCard, PreviewBlogPost } from "./BlogPreviewRenderer";
import useVenuesById from "./useVenuesById";

/**
 * Full preview modal for a blog post draft. Shows:
 *   - Card: how the post will appear in the public blog grid
 *   - Post: how the post will appear on its own detail page
 *
 * Users can switch language (ES/EN) and view (Card/Post) independently.
 */
export default function BlogPreviewModal({ open, onClose, post, authorName }) {
  const [lang, setLang] = useState("es");
  const [view, setView] = useState("post");
  const [allTags, setAllTags] = useState([]);
  // Venues para las tarjetas de los bloques ```venues del body.
  const venuesById = useVenuesById(open);

  // Load tag registry once for label resolution
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "blogTags"));
        if (cancelled) return;
        setAllTags(
          snap.docs.map((d) => ({
            slug: d.id,
            labels: d.data().labels || { es: d.id, en: d.id },
          }))
        );
      } catch (e) {
        console.error("Failed to load blogTags for preview", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  // If current lang has no translation, fall back to the one that exists
  const availableLangs = useMemo(() => {
    const list = [];
    if (post?.translations?.es) list.push("es");
    if (post?.translations?.en) list.push("en");
    return list;
  }, [post]);

  useEffect(() => {
    if (availableLangs.length && !availableLangs.includes(lang)) {
      setLang(availableLangs[0]);
    }
  }, [availableLangs, lang]);

  if (!open) return null;

  const PILL_GROUP = "inline-flex items-center gap-1 rounded-full bg-gray-100 p-1";
  const PILL_IDLE =
    "rounded-full px-4 py-1.5 text-sm text-gray-600 transition hover:text-[#1b4d2e]";
  const PILL_ACTIVE =
    "rounded-full bg-[#1b4d2e] px-4 py-1.5 text-sm font-semibold text-white shadow-sm";

  return (
    <Dialog open={open} onClose={onClose} size="5xl">
      <DialogTitle>Vista previa</DialogTitle>

      <DialogBody>
        {/* Toggles */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className={PILL_GROUP}>
            {[
              { k: "post", label: "Post completo" },
              { k: "card", label: "Tarjeta en grid" },
            ].map((v) => (
              <button
                key={v.k}
                type="button"
                onClick={() => setView(v.k)}
                className={view === v.k ? PILL_ACTIVE : PILL_IDLE}
              >
                {v.label}
              </button>
            ))}
          </div>

          <div className={PILL_GROUP}>
            {[
              { k: "es", label: "Español" },
              { k: "en", label: "English" },
            ].map((l) => {
              const disabled = !availableLangs.includes(l.k);
              return (
                <button
                  key={l.k}
                  type="button"
                  onClick={() => !disabled && setLang(l.k)}
                  disabled={disabled}
                  className={`${lang === l.k ? PILL_ACTIVE : PILL_IDLE} ${
                    disabled ? "cursor-not-allowed opacity-40" : ""
                  }`}
                >
                  {l.label}
                </button>
              );
            })}
          </div>
        </div>

        <p className="mb-3 text-xs text-gray-500">
          Esta es una vista previa aproximada. La fecha y el autor se muestran
          con valores del momento actual. El botón "Volver al blog" y el footer
          no se reemplazan para no recargar el modal.
        </p>

        {/* Preview canvas — neutral background to match public site feel */}
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 md:p-6">
          {view === "card" ? (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              <PreviewBlogCard
                post={post}
                locale={lang}
                allTags={allTags}
              />
              {/* Ghost placeholders so the card shows at realistic grid width */}
              <div className="hidden h-full rounded-xl border border-dashed border-gray-300 md:block" />
              <div className="hidden h-full rounded-xl border border-dashed border-gray-300 lg:block" />
            </div>
          ) : (
            <div className="rounded-xl bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden">
              <PreviewBlogPost
                post={post}
                locale={lang}
                allTags={allTags}
                authorName={authorName}
                venuesById={venuesById}
              />
            </div>
          )}
        </div>
      </DialogBody>
    </Dialog>
  );
}
